---
title: Easier Agent Integrations and Safer Checkpoints
description: The Entire CLI makes it easier to add agents, improves Gemini transcript parsing, redacts secrets across stored session data, and fixes Git hook handling and checkpoint reliability.
category: Changelog
author: Marvin
---

Beep, boop. Welcome to Dispatch 0x0002.

Last week, we introduced Checkpoints to the world — our first primitive designed to tackle one of the biggest problems in agent-driven development: context. Today, conversations, reasoning, and file changes are fragmented or lost between sessions. As agents generate hundreds or even thousands of lines per interaction, that context loss compounds quickly. Agents retrace steps, duplicate reasoning, waste tokens, and lose the thread of decisions made hours or days earlier.

**To bridge this gap, the [Entire CLI](https://github.com/entireio/cli) automatically captures agent sessions, creating a semantic reasoning layer that binds agent context to Git on every push. Resulting in what we call Checkpoints.**
This week we focused on making the Entire CLI easier to integrate with other agents, including a refactor that simplifies how external tools plug into the workflow and interact with grouped changes. **Stay tuned, new agent support shipping very soon.**

And with your help, we shipped a variety bug fixes, performance improvements, and [key updates](https://github.com/entireio/cli/releases):

## Expanding Agent Integrations and Capabilities

- The Entire CLI codebase has a stronger multi-agent framework, enabling faster integration of new agents.
- The Entire CLI integration with the Gemini CLI supports full transcript parsing, Checkpointing, and session explanations.

## Secret Detection

- Checkpoints runs layered secret scanning using gitleaks patterns combined with entropy analysis.
- Secrets are redacted across all persistence paths — including shadow branches, metadata, and condensed logs — ensuring sensitive data does not leak into Git history.
  - Thank you to [Hi120ki](https://github.com/hi120ki), [andreidavid](https://github.com/andreidavid), and [Pythoninthegrass](https://github.com/pythoninthegrass)!

## Smarter Hook Management

- Hook installation is more resilient, with automatic resolution of hook directories supporting worktrees and custom configurations using `git --git-path hooks`.
- Existing Git hooks that the Entire CLI does not recognize are preserved.
- External hook managers such as Husky or Lefthook are detected during `entire enable`, with a warning before anything is overwritten.
  - Shout out to [AlienKevin](https://github.com/AlienKevin), [Camwest](https://github.com/camwest), [SchutteJan](https://github.com/SchutteJan), and [FSM1](https://github.com/FSM1)!

## Checkpoint Reliability

- Subagent task file modifications are correctly captured in Checkpoints.
- Deleted files in task Checkpoints are properly tracked.
- Each Checkpoint produces exactly one commit, eliminating noise in Checkpoint history.
- Git-ignored files such as `node_modules` are no longer tracked in session metadata.
  - Danke, [BootstrapBits](https://github.com/BootstrapBits)!

## Improved `entire enable`

- The setup experience has been redesigned with clearer prompts and better defaults, making it faster and more intuitive to get started in a new repo.
  - Big thank you to [Jayanth-iyer](https://github.com/jayanth-iyer), [TyceHerrman](https://github.com/TyceHerrman), and [Marcodicro-dp](https://github.com/marcodicro-dp)!

## Session Restore Fixes

- Sessions that previously failed to restore after repo changes (stash, pull, rebase) recover correctly.
- Full Checkpoint history is preserved across branch operations.
  - HT, [Jaydenfyi](https://github.com/jaydenfyi)!

## Empty Repo Support

- Improved messaging when the Entire CLI is used in a freshly initialized repository that has no commits yet.

In the coming week, we’ll focus on integrating the [Entire CLI](https://github.com/entireio/cli) across the broader agent ecosystem — so every developer can have pluggable, independent context natively tied to their Git repository, no matter which agent they use.

Boop.
